import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, MapPin, ArrowRight, RefreshCw, Bot } from 'lucide-react';
import { apiClient } from '../api';
import { Button } from './ui/button';
import FoundingMemberBadge from './FoundingMemberBadge';

const AIMatchOfDay = () => {
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);

  const fetchMatch = async () => {
    try {
      setErr(null);
      const r = await apiClient.get('/matchmaking/today');
      setMatch(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No match available yet');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchMatch();
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await apiClient.post('/matchmaking/refresh');
    } catch (e) { /* ignore */ }
    fetchMatch();
  };

  if (loading) {
    return (
      <section className="mb-6" data-testid="aimatch-loading">
        <div className="rounded-2xl bg-gradient-to-br from-fnl-pink/10 via-fnl-purple/60 to-fnl-purple/30 border border-fnl-pink/30 p-6 h-44 animate-pulse" />
      </section>
    );
  }

  if (err || !match) {
    return null; // Hide silently for profileless users
  }

  return (
    <section className="mb-8" data-testid="aimatch-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-fnl-pink/20 border border-fnl-pink/50 text-fnl-pink text-[10px] font-bold uppercase tracking-wider">
          <Bot className="w-3 h-3" />AI Pick
        </div>
        <h2 className="text-xl font-bold text-white">Match of the day</h2>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="ml-auto text-purple-300 hover:text-fnl-pink disabled:opacity-40 transition-colors"
          aria-label="Refresh match"
          data-testid="aimatch-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <Link
        to={`/profile/${match.user_id}`}
        className="block group"
        data-testid="aimatch-link"
      >
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-fnl-pink/15 via-fnl-purple to-fnl-dark border border-fnl-pink/40 shadow-xl shadow-fnl-pink/10 hover:shadow-fnl-pink/25 transition-shadow">
          <div className="flex flex-col sm:flex-row">
            {/* Photo */}
            <div className="sm:w-56 shrink-0 relative aspect-[4/5] sm:aspect-auto overflow-hidden bg-fnl-purple-light">
              {match.photo_base64 ? (
                <img
                  src={match.photo_base64}
                  alt={match.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Sparkles className="w-16 h-16 text-fnl-pink/40" />
                </div>
              )}
              <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-fnl-dark/80 backdrop-blur-sm border border-fnl-pink/40">
                <Sparkles className="w-3 h-3 text-fnl-pink" />
                <span className="text-white text-[10px] font-bold tracking-wider">{match.score}% match</span>
              </div>
              {match.founding_member && (
                <div className="absolute top-3 right-3"><FoundingMemberBadge size="sm" /></div>
              )}
              <div className="absolute inset-0 sm:hidden bg-gradient-to-t from-fnl-dark via-fnl-dark/30 to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 sm:hidden text-white">
                <div className="font-bold text-xl leading-tight">{match.name}, {match.age}</div>
                {match.location && (
                  <div className="text-xs text-purple-200 flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" />{match.location}
                  </div>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 p-5 sm:p-6 flex flex-col justify-between">
              <div>
                <div className="hidden sm:block mb-2">
                  <div className="text-2xl font-bold text-white leading-tight">{match.name}, {match.age}</div>
                  {match.location && (
                    <div className="text-sm text-purple-300 flex items-center gap-1 mt-1">
                      <MapPin className="w-3.5 h-3.5" />{match.location}
                    </div>
                  )}
                </div>
                <p
                  className="text-sm text-purple-100 leading-relaxed italic"
                  data-testid="aimatch-reason"
                >
                  "{match.reason}"
                </p>
                {match.interests && match.interests.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3" data-testid="aimatch-interests">
                    {match.interests.map((i) => (
                      <span
                        key={i}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-fnl-pink/15 text-fnl-pink border border-fnl-pink/30 font-semibold"
                      >
                        {i}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between mt-4">
                <span className="text-[10px] text-purple-400 uppercase tracking-wider font-semibold">
                  {match.ai ? 'Curated by Flirty AI' : 'Best heuristic pick today'}
                </span>
                <Button
                  size="sm"
                  className="bg-fnl-pink hover:bg-fnl-pink-light text-white font-semibold gap-1"
                >
                  View profile <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </section>
  );
};

export default AIMatchOfDay;
